
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset,
  onAuthStateChanged,
  signOut,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  applyActionCode,
  checkActionCode
} from "firebase/auth";

export const resetPassword = async (oobCode: string, newPassword: string): Promise<void> => {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (error: any) {
    console.error("Gabim gjatë resetimit të fjalëkalimit:", error);
    if (error.code === 'auth/expired-action-code') {
      throw new Error("Kodi ka skaduar. Ju lutem kërkoni një link të ri.");
    }
    if (error.code === 'auth/invalid-action-code') {
      throw new Error("Kodi është i pavlefshëm.");
    }
    throw error;
  }
};
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc,
  updateDoc,
  increment,
  DocumentData,
  QueryDocumentSnapshot
} from "firebase/firestore";
import { 
  ref, 
  uploadString, 
  getDownloadURL 
} from "firebase/storage";
import { auth, db, storage, handleFirestoreError, OperationType } from "./firebase";
import { Story, User } from "../types";

let isFirestoreAvailable = true;

const setFirestoreUnavailable = (error: any) => {
  if (isFirestoreAvailable) {
    console.warn("Lidhja me Firestore dështoi. Duke kaluar në LocalStorage mode.", error);
    isFirestoreAvailable = false;
  }
};

// --- STORAGE MANAGEMENT ---

export const uploadBase64ToStorage = async (path: string, base64Data: string, contentType: string): Promise<string> => {
  if (!auth.currentUser) {
    console.warn("Përdoruesi nuk është i autentikuar. Ngarkimi në Storage dështoi.");
    return `data:${contentType};base64,${base64Data}`;
  }

  try {
    const storageRef = ref(storage, path);
    // Shto një timeout për të shmangur bllokimin e gjatë nëse rrjeti është i dobët
    await uploadString(storageRef, base64Data, 'base64', { contentType });
    const downloadUrl = await getDownloadURL(storageRef);
    
    // Cache the blob in session to bypass CORS later
    try {
      const { cacheAudioBlob } = await import('./geminiService');
      const binary = window.atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: contentType });
      cacheAudioBlob(downloadUrl, blob);
    } catch (cacheErr) {
      console.warn("Dështoi ruajtja në cache-in e sesionit:", cacheErr);
    }
    
    return downloadUrl;
  } catch (error: any) {
    // Nëse dështon ngarkimi (p.sh. probleme me lejet ose rrjetin), përdorim fallback
    if (error.code === 'storage/unauthorized') {
      console.warn("Storage unauthorized - duke përdorur fallback lokal.");
    } else {
      console.warn("Gabim gjatë ngarkimit në Storage (duke përdorur fallback):", error.message);
    }
    
    return `data:${contentType};base64,${base64Data}`;
  }
};

// --- STORY MANAGEMENT ---

export const shareStory = async (story: Story): Promise<string> => {
  if (isFirestoreAvailable) {
    try {
      // Krijo një kopje për koleksionin publik
      const publicStory = {
        ...story,
        isPublic: true,
        sharedAt: Date.now()
      };
      
      const path = `shared_stories/${story.id}`;
      await setDoc(doc(db, "shared_stories", story.id), publicStory);
      return story.id;
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.WRITE, `shared_stories/${story.id}`);
      }
      console.error("Gabim gjatë shpërndarjes së përrallës:", e);
      throw e;
    }
  }
  throw new Error("Firestore nuk është i disponueshëm për shpërndarje.");
};

export const getPublicStory = async (storyId: string): Promise<Story | null> => {
  if (isFirestoreAvailable) {
    try {
      const docRef = doc(db, "shared_stories", storyId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as Story;
      }
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.GET, `shared_stories/${storyId}`);
      }
      console.error("Gabim gjatë marrjes së përrallës publike:", e);
    }
  }
  return null;
};

export const saveStoryToDB = async (story: Story): Promise<void> => {
  if (isFirestoreAvailable) {
    try {
      // Krijo një kopje të pastër për Firestore
      const firestoreStory = {
        ...story,
        parts: story.parts.map(p => ({
          ...p,
          imageUrl: p.imageUrl
        })),
        audioBase64: story.audioBase64
      };
      
      const path = `users/${story.userId}/perrallat/${story.id}`;
      await setDoc(doc(db, "users", story.userId, "perrallat", story.id), firestoreStory);
      return;
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.WRITE, `users/${story.userId}/perrallat/${story.id}`);
      }
      console.warn("Dështoi ruajtja në Firestore, duke provuar LocalStorage:", e.message);
      if (e.code === 'not-found' || e.message?.includes('not exist') || e.message?.includes('offline') || e.code === 'permission-denied' || e.code === 'resource-exhausted') {
        setFirestoreUnavailable(e);
      }
    }
  }
  
  try {
    const localStories = JSON.parse(localStorage.getItem(`stories_${story.userId}`) || '[]');
    // Kontrollo nëse ekziston për të shmangur dublikatet
    const index = localStories.findIndex((s: any) => s.id === story.id);
    if (index >= 0) {
      localStories[index] = story;
    } else {
      localStories.push(story);
    }
    localStorage.setItem(`stories_${story.userId}`, JSON.stringify(localStories));
  } catch (err) {
    console.error("Dështoi ruajtja në LocalStorage:", err);
  }
};

export const getStoriesByUserId = async (userId: string): Promise<Story[]> => {
  if (isFirestoreAvailable) {
    try {
      const path = `users/${userId}/perrallat`;
      const querySnapshot = await getDocs(collection(db, "users", userId, "perrallat"));
      const stories: Story[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        stories.push(doc.data() as Story);
      });
      return stories.sort((a, b) => b.createdAt - a.createdAt);
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.LIST, `users/${userId}/perrallat`);
      }
      if (e.code === 'not-found' || e.message?.includes('not exist') || e.code === 'permission-denied') setFirestoreUnavailable(e);
      else throw e;
    }
  }
  return JSON.parse(localStorage.getItem(`stories_${userId}`) || '[]')
    .sort((a: any, b: any) => b.createdAt - a.createdAt);
};

export const deleteStoryFromDB = async (userId: string, storyId: string): Promise<void> => {
  if (isFirestoreAvailable) {
    try {
      await deleteDoc(doc(db, "users", userId, "perrallat", storyId));
      // Provo të fshish edhe nga shared_stories nëse ekziston
      try {
        await deleteDoc(doc(db, "shared_stories", storyId));
      } catch (e: any) {
        if (e.code === 'permission-denied') {
          handleFirestoreError(e, OperationType.DELETE, `shared_stories/${storyId}`);
        }
      }
      return;
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.DELETE, `users/${userId}/perrallat/${storyId}`);
      }
      if (e.code === 'not-found' || e.code === 'permission-denied') setFirestoreUnavailable(e);
    }
  }
  const localStories = JSON.parse(localStorage.getItem(`stories_${userId}`) || '[]');
  const filtered = localStories.filter((s: Story) => s.id !== storyId);
  localStorage.setItem(`stories_${userId}`, JSON.stringify(filtered));
};

// --- USER MANAGEMENT ---

export const saveUserToDB = async (user: User): Promise<void> => {
  localStorage.setItem(`user_${user.id}`, JSON.stringify(user));
  if (isFirestoreAvailable) {
    try {
      await setDoc(doc(db, "users", user.id), user, { merge: true });
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.id}`);
      }
      if (e.code === 'not-found' || e.message?.includes('not exist') || e.code === 'permission-denied') setFirestoreUnavailable(e);
    }
  }
};

export const getUserFromDB = async (id: string): Promise<User | null> => {
  let user: User | null = null;
  if (isFirestoreAvailable) {
    try {
      const docRef = doc(db, "users", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) user = docSnap.data() as User;
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        handleFirestoreError(e, OperationType.GET, `users/${id}`);
      }
      if (e.code === 'not-found' || e.message?.includes('not exist') || e.code === 'permission-denied') setFirestoreUnavailable(e);
    }
  }
  if (!user) {
    const localUser = localStorage.getItem(`user_${id}`);
    if (localUser) user = JSON.parse(localUser);
  }
  return user;
};

export const verifyEmailWithCode = async (oobCode: string): Promise<void> => {
  try {
    await applyActionCode(auth, oobCode);
    // Reload user if logged in
    if (auth.currentUser) {
      await auth.currentUser.reload();
      const dbUser = await getUserFromDB(auth.currentUser.uid);
      if (dbUser && !dbUser.isVerified) {
        await saveUserToDB({ ...dbUser, isVerified: true });
      }
    }
  } catch (error: any) {
    console.error("Gabim gjatë verifikimit me kod:", error);
    throw error;
  }
};

export const resendVerificationEmail = async (): Promise<void> => {
  const user = auth.currentUser;
  if (user) {
    console.log("Po ridërgojmë email-in e verifikimit te:", user.email);
    try {
      await sendEmailVerification(user);
      console.log("Email-i u dërgua me sukses.");
    } catch (error: any) {
      console.error("Gabim gjatë ridërgimit:", error);
      if (error.code === 'auth/too-many-requests') {
        throw new Error("Keni bërë shumë kërkesa. Ju lutem prisni pak minuta.");
      }
      throw error;
    }
  } else {
    throw new Error("Asnjë përdorues i kyçur.");
  }
};

export const checkEmailVerificationStatus = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (user) {
    try {
      await user.reload();
      const refreshedUser = auth.currentUser;
      const isVerified = refreshedUser?.emailVerified || false;
      if (isVerified) {
        const dbUser = await getUserFromDB(user.uid);
        if (dbUser && !dbUser.isVerified) {
          await saveUserToDB({ ...dbUser, isVerified: true });
        }
      }
      return isVerified;
    } catch (e) {
      return false;
    }
  }
  return false;
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
    if (fbUser) {
      const dbUser = await getUserFromDB(fbUser.uid);
      if (dbUser) {
        // Migration/Promo logic: Give 2 tokens if user hasn't received promo yet
        if (!dbUser.hasReceivedPromo) {
          const updated = { 
            ...dbUser, 
            tokens: Math.max(dbUser.tokens, 2), 
            hasReceivedPromo: true,
            justReceivedPromo: true // Flag for UI to show banner
          } as User;
          await saveUserToDB({ ...dbUser, tokens: Math.max(dbUser.tokens, 2), hasReceivedPromo: true });
          callback(updated);
          return;
        }
        
        if (!dbUser.isVerified && fbUser.emailVerified) {
          const updated = { ...dbUser, isVerified: true };
          await saveUserToDB(updated);
          callback(updated);
        } else {
          callback(dbUser);
        }
      } else {
        const newUser: User = {
          id: fbUser.uid,
          email: fbUser.email!,
          name: fbUser.displayName || fbUser.email!.split('@')[0],
          tokens: 2,
          isLoggedIn: true,
          isVerified: fbUser.emailVerified,
          joinedAt: Date.now(),
          hasReceivedPromo: true,
          justReceivedPromo: true // Flag for UI to show banner
        };
        await saveUserToDB(newUser);
        callback(newUser);
      }
    } else {
      callback(null);
    }
  });
};

export const loginUser = async (email: string, password?: string): Promise<User> => {
  if (!password) throw new Error("Fjalëkalimi është i detyrueshëm.");
  const cleanEmail = email.trim();
  
  try {
    const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
    const fbUser = userCredential.user;
    let dbUser = await getUserFromDB(fbUser.uid);
    if (!dbUser) {
      dbUser = {
        id: fbUser.uid,
        email: fbUser.email!,
        name: fbUser.displayName || cleanEmail.split('@')[0],
        tokens: 2,
        isLoggedIn: true,
        isVerified: fbUser.emailVerified,
        joinedAt: Date.now(),
        hasReceivedPromo: true,
        justReceivedPromo: true // Flag for UI to show banner
      };
      await saveUserToDB(dbUser);
    }
    return dbUser;
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential' || error.code === 'auth/invalid-login-credentials') {
      throw new Error("Email or password is incorrect");
    }
    throw error;
  }
};

export const registerUser = async (email: string, password?: string, name?: string): Promise<User> => {
  if (!password) throw new Error("Fjalëkalimi është i detyrueshëm.");
  const cleanEmail = email.trim();
  
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
    const fbUser = userCredential.user;
    
    // Konfigurimi i Action URL për të ridrejtuar te aplikacioni ynë
    const actionCodeSettings = {
      url: `${window.location.origin}/?mode=verifyEmail`,
      handleCodeInApp: true,
    };
    
    await sendEmailVerification(fbUser, actionCodeSettings);
    
    const newUser: User = {
      id: fbUser.uid,
      email: fbUser.email!,
      name: name || cleanEmail.split('@')[0],
      tokens: 2,
      isLoggedIn: true,
      isVerified: false,
      joinedAt: Date.now(),
      hasReceivedPromo: true,
      justReceivedPromo: true // Flag for UI to show banner
    };
    await saveUserToDB(newUser);
    return newUser;
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error("User already exists. Please sign in");
    }
    throw error;
  }
};

export const authenticateUser = async (email: string, password?: string, name?: string): Promise<User> => {
  // Për kompatibilitet prapa, por do të përdorim loginUser/registerUser direkt
  return loginUser(email, password);
};

export const loginWithGoogle = async (): Promise<User> => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const fbUser = result.user;
    let dbUser = await getUserFromDB(fbUser.uid);
    
    if (!dbUser) {
      dbUser = {
        id: fbUser.uid,
        email: fbUser.email!,
        name: fbUser.displayName || fbUser.email!.split('@')[0],
        tokens: 2,
        isLoggedIn: true,
        isVerified: true, // Google users are considered verified
        joinedAt: Date.now(),
        hasReceivedPromo: true,
        justReceivedPromo: true // Flag for UI to show banner
      };
      await saveUserToDB(dbUser);
    }
    return dbUser;
  } catch (error: any) {
    console.error("Google Auth Error:", error);
    throw error;
  }
};

export const logoutUser = async () => {
  await signOut(auth);
};

export const forgotPassword = async (email: string): Promise<void> => {
  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/?mode=resetPassword`,
      handleCodeInApp: true,
    };
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  } catch (error: any) {
    console.error("Gabim gjatë dërgimit të email-it për fjalëkalimin:", error);
    if (error.code === 'auth/user-not-found') {
      throw new Error("Ky email nuk është i regjistruar.");
    }
    if (error.code === 'auth/too-many-requests') {
      throw new Error("Keni bërë shumë kërkesa. Ju lutem prisni pak minuta.");
    }
    throw error;
  }
};
